import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ErrorMessage, useFormik } from 'formik';
import * as yup from 'yup';
import {
    Alert,
    Avatar,
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    LinearProgress,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    Autocomplete,
    Chip,
    CircularProgress,
    CardContent,
    Card,
} from '@mui/material';
import { Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { saveToken, saveUser } from '../core/services/authService';
import leadsService from '../core/services/leadsService';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import toast from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';
import OutlinedInput from '@mui/material/OutlinedInput';
import { debounce, throttle } from 'lodash';
const validationSchema = yup.object({
    email: yup.string().email('Enter a valid email').required('Email is required'),
    name: yup.string().required('First Name is required'),
    last_name: yup.string().required('Last name is required'),
    phone: yup.string().required('Phone number is required')
        .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be in a valid format, e.g., (123) 456-7890')
    // prefered_location: yup.string().required('Select prefered location'),
    // image: yup.string().required('Image is required'),
    // password: yup
    //     .string()
    //     .required('Password is required')
    //     .min(6, 'Password must be at least 6 characters long')
    //     .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/, {
    //         excludeEmptyString: true, // Only apply this rule if the password is not empty
    //         message: 'Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character',
    //     }),
});

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '22px',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    width: 'auto',
    height: '80%',
    overflow: 'auto',
};
const AdminAgentEdit = ({ name, last_name, email, phone, cover_video, profile_url, subscription_type, id, getISAAgents, prefered_location, organization_id, bio, image }) => {
    const [open, setOpen] = React.useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showAlert, setShowAlert] = useState(false);
    const [alertType, setAlertType] = useState('error');
    const [alertMesage, setAlertMessage] = useState('Something went wrong');
    const [loading, setLoading] = useState(false);
    const [allLocation, setAllLocation] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [searchResults, setSearchResults] = useState([]);
    const lastScrollTopRef = useRef(0);

    const preferedLocationStrings = prefered_location?.map(loc => loc.id) || [];

    const searchTermRef = useRef('');
    const abortControllerRef = useRef(null);
    useEffect(() => {
        if (showAlert) {
            setTimeout(() => {
                setShowAlert(false);
                setAlertType('error');
            }, 3000);
        }
    }, [showAlert]);
    const formik = useFormik({
        initialValues: {
            email: email || '',
            name: name || '',
            last_name: last_name || '',
            phone: phone || '',
            image: '',
            password: '',
            cover_video: cover_video || '',
            profile_url: profile_url || '',
            subscription_type: subscription_type || '',
            id: id || '',
            prefered_location: preferedLocationStrings,
            bio: bio || '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (showAlert) setShowAlert(false);
            try {

                setLoading(true);
                const res = await leadsService.UpdateIcaAgent(values);
                if (res?.data?.success === true) {

                    setShowAlert(true);
                    toast.success(res?.data?.message)
                    setAlertType('success');
                    formik.resetForm();
                    getISAAgents(id);
                    setOpen(false);
                    fetchLocations();
                    return;
                }
                if (res?.response.data) {
                    setShowAlert(true);
                    setAlertMessage(res?.response?.data?.message);
                }
            } catch (error) {
                setShowAlert(true);
                toast.error(error.message)
            } finally {
                setLoading(false);
            }
        },
        enableReinitialize: true,
    });
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);




    const fetchLocations = async (page = 1, searchTerm = '', organization_id, signal) => {
        setLoading(true);
        try {
            const isPaginated = 1;
            const res = await leadsService.getLocation({ currentPage: page, searchTerm, isPaginated, organization_id, signal });
            if (res.data.success && Array.isArray(res.data.data.data)) {
                setSearchResults(prevResults => {
                    if (page === 1) {
                        return res.data.data.data;
                    }
                    // If not the first page, append new data to existing results
                    return [...prevResults, ...res.data.data.data];
                });

                setCurrentPage(page);
                setHasMore(res.data.data.data.length > 0); // Check if there are more pages to load

                // Update allLocation with fetched data
                const newLocationsSet = new Set(res.data.data.data.map(loc => loc.id));
                setAllLocation(prev => [
                    ...prev.filter(loc => !newLocationsSet.has(loc.id)), // Remove old data that is not in new locations
                    ...res.data.data.data // Add new data
                ]);

                // if (page === 1) {
                //     setSearchTerm('');
                // }

            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(error.message);
                toast.error(error.message);
            }
        } finally {
            setLoading(false);
        }
    };


    const debouncedFetchLocations = useCallback(    // Use a debounced version of fetchLocations
        debounce((page, searchTerm, organization_id) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            fetchLocations(page, searchTerm, organization_id, abortController.signal);
        }, 300), []
    );

    useEffect(() => {
        debouncedFetchLocations(currentPage, searchTerm, organization_id);
    }, [currentPage, searchTerm, organization_id, debouncedFetchLocations]);

    const handleInputChange = (event, value) => {
        searchTermRef.current = value;
        setSearchTerm(value);
        if (value.trim() !== '') {

            debouncedFetchLocations(1, value);
        } else {
            // setSearchResults([]);
            setCurrentPage(1);
            setHasMore(false);
        }
    };


    const handleScroll = (e) => {
        if (loading || !hasMore) return; // If loading or no more data, do nothing

        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isScrollingDown = scrollTop > lastScrollTopRef.current;

        if (isScrollingDown && scrollTop + clientHeight >= scrollHeight - 5) {
            debouncedFetchLocations(currentPage + 1);
        }
        lastScrollTopRef.current = scrollTop;
    };


    const handleChange = (event, value) => {
        const selectedLocation = value[value.length - 1];
        if (!selectedLocation) return;
    
        // Get the current selected cities count
        const selectedCities = formik.values.prefered_location
            .map(id => allLocation.find(loc => loc.id === id))
            .filter(loc => loc?.status === "City");
    
        let maxCities = 0;
        if (formik.values.subscription_type === "founder") maxCities = 3;
        else if (formik.values.subscription_type === "premier") maxCities = 5;
        else if (formik.values.subscription_type === "elite") maxCities = 10;
    
        // Ensure the limit is respected
        if (selectedLocation.status === "City" && selectedCities.length >= maxCities) {
            return toast.error(`You can only select up to ${maxCities} cities for your subscription type.`);
            
        }
    
        // Add or remove the location
        if (formik.values.prefered_location.includes(selectedLocation.id)) {
            formik.setFieldValue(
                'prefered_location',
                formik.values.prefered_location.filter(id => id !== selectedLocation.id)
            );
        } else {
            formik.setFieldValue(
                'prefered_location',
                [...formik.values.prefered_location, selectedLocation.id]
            );
    
            // Add the selected location to allLocation if it's not already present
            if (!allLocation.find(loc => loc.id === selectedLocation.id)) {
                setAllLocation([...allLocation, selectedLocation]);
            }
        }
    };
    

    const handleDelete = (deletedId) => {
        formik.setFieldValue(
            "prefered_location",
            formik.values.prefered_location.filter(id => id !== deletedId) // Remove only the matching location ID
        );

        formik.submitForm();
    };





    return (
        <div>
            {name ? (
                <Button
                    onClick={handleOpen}
                    variant="contained"
                    sx={{ marginLeft: 'auto', backgroundColor: '#5379BD', borderRadius: '10px', textTransform: 'capitalize', fontFamily: 'inherit', padding: {
                        xs: '3px',
                        sm: '8px',
                        md: '6px',
                    },
                    fontSize: {
                        xs: '10px',
                        sm: '14px',
                        md: '16px',
                    } }}
                >
                    Edit Profile
                </Button>
            ) : (
                <p></p>
            )}
            <Modal open={open} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box sx={style} className="modal-resp-settings">
                    <IconButton style={{ position: 'absolute', top: '0px', right: '20px' }} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                    <div>

                    </div>
                    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} sx={{ marginTop: '18px' }}>
                        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                            <Box position={'relative'}>
                                {showAlert && (
                                    <Alert
                                        sx={{ position: 'absolute', top: '-90px', left: '28%' }}
                                        severity={alertType}
                                        onClose={() => {
                                            setShowAlert(false);
                                        }}
                                    >
                                        {alertMesage}
                                    </Alert>
                                )}

                                <Stack alignItems={'center'}>
                                    <Paper sx={{ p: 2, borderRadius: '22px', }}>
                                        <Typography
                                            display={'flex'}
                                            justifyContent={'center'}
                                            alignItems={'center'}
                                            sx={{ fontSize: '32px', fontWeight: '700', mb: 5 }}>Edit Agent</Typography>
                                        <form className='modal-form-settings' onSubmit={formik.handleSubmit}>
                                            <Stack
                                                display={'flex'} justifyContent={'center'} alignItems={'center'}
                                                spacing={2}>
                                                <Stack
                                                    onClick={() => document.getElementById('image').click()}
                                                    sx={{
                                                        '&:hover': {
                                                            cursor: 'pointer',
                                                        },
                                                    }}
                                                    direction={'row'}
                                                    spacing={1}
                                                    alignItems={'center'}
                                                >
                                                    <Avatar
                                                        sx={{
                                                            border:
                                                                formik.touched.image && formik.errors.image
                                                                    ? '3px solid #d32f2f'
                                                                    : formik.values.image
                                                                        ? '3px solid green'
                                                                        : '',
                                                            width: '60px',
                                                            height: '60px',
                                                            bgcolor: formik.touched.image && formik.errors.image ? '#d32f2f' : 'rgb(25, 118, 210)',
                                                        }}
                                                        alt="A"
                                                        src={formik?.values?.image ? URL.createObjectURL(formik.values.image) : `https://iconnctapp.com/` + image}
                                                    >
                                                        <CloudUploadIcon />
                                                    </Avatar>
                                                    <Typography color={'gray'}> {formik.values.image ? 'Change Image' : 'Upload image'} </Typography>
                                                </Stack>
                                                <small style={{ color: '#d32f2f' }}>{formik.touched.image && formik.errors.image}</small>
                                                <input
                                                    style={{ display: 'none' }}
                                                    id="image"
                                                    name="image"
                                                    type="file"
                                                    onChange={(event) => {
                                                        const file = event.currentTarget.files[0];
                                                        const allowedFormats = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];

                                                        if (file) {
                                                            // Check file type
                                                            if (!allowedFormats.includes(file.type)) {
                                                                formik.setFieldValue('image', null);
                                                                toast.error('Invalid image format. Only jpeg, png, and jpg formats are allowed.');
                                                                return;
                                                            }

                                                            // Check file size
                                                            if (file.size > 250 * 1024) { // 20KB in bytes
                                                                formik.setFieldValue('image', null);
                                                                toast.error('Image size should be less than 250KB.');
                                                                return;
                                                            }

                                                            // If both checks pass, set the field value
                                                            formik.setFieldValue('image', file);
                                                        }
                                                    }}
                                                />

                                                <TextField
                                                    fullWidth
                                                    id="name"
                                                    name="name"
                                                    label="First Name"
                                                    value={formik.values.name}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                                    helperText={formik.touched.name && formik.errors.name}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    id="last_name"
                                                    name="last_name"
                                                    label="Last name"
                                                    value={formik.values.last_name}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                                                    helperText={formik.touched.last_name && formik.errors.last_name}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    id="email"
                                                    name="email"
                                                    label="Email"
                                                    value={formik.values.email}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                                    helperText={formik.touched.email && formik.errors.email}
                                                    inputProps={{
                                                        readOnly: true,
                                                    }}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    id="phone"
                                                    name="phone"
                                                    label="Phone #"
                                                    value={formik.values.phone}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                                                    helperText={formik.touched.phone && formik.errors.phone}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                                {formik.values.subscription_type !== 'founder' && (
                                                    <TextField
                                                        fullWidth
                                                        id="cover_video"
                                                        name="cover_video"
                                                        label="Cover Video"
                                                        value={formik.values.cover_video}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        sx={{
                                                        width: '100%',
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: '12px',
                                                            },
                                                        }}
                                                    />
                                                )}
                                                <TextField
                                                    fullWidth
                                                    id="profile_url"
                                                    name="profile_url"
                                                    label="Searchable Link"
                                                    value={formik.values.profile_url}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    id="bio"
                                                    name="bio"
                                                    label="Bio"
                                                    multiline
                                                    row={2}
                                                    variant="outlined"
                                                    value={formik.values.bio}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.bio && Boolean(formik.errors.bio)}
                                                    helperText={formik.touched.bio && formik.errors.bio}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    id="password"
                                                    name="password"
                                                    label="Update Password"
                                                    value={formik.values.password}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                                    helperText={formik.touched.password && formik.errors.password}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                        },
                                                    }}
                                                />
                                                <FormControl
                                                    error={formik.touched.prefered_location && Boolean(formik.errors.prefered_location) ? true : false}
                                                    sx={{ m: 1, width: 300 }}
                                                >

                                                    <div onScroll={handleScroll} style={{ maxHeight: '300px', overflow: 'auto' }}>
                                                        <Autocomplete
                                                            multiple
                                                            options={searchResults}
                                                            getOptionLabel={(option) => option?.region_name}
                                                            onChange={handleChange}
                                                            onInputChange={handleInputChange}
                                                            renderInput={(params) => <TextField {...params} label="Search Locations" variant="outlined" />}
                                                            loading={loading}
                                                            ListboxProps={{ onScroll: handleScroll }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: '12px',
                                                                },
                                                            }}
                                                        />
                                                        <Box mt={2}>
                                                            <CardContent>
                                                                {prefered_location?.map(locationId => {
                                                                    const location = allLocation?.find(loc => loc?.id === locationId);  // Try to find the location in the existing list
                                                                    // If location is not found in allLocation, you might want to handle it differently
                                                                    if (!location) {
                                                                        return (
                                                                            <Chip
                                                                                key={locationId?.id} // Ensure the key is a unique ID
                                                                                label={locationId?.region_name} // Show the region name
                                                                                onDelete={() => handleDelete(locationId.id)} // Pass only the location ID
                                                                                style={{ margin: 2 }}
                                                                            />
                                                                        );
                                                                    }

                                                                    return (
                                                                        location && (
                                                                            <Chip
                                                                                key={locationId?.id} // Ensure the key is a unique ID
                                                                                label={locationId?.region_name} // Show the region name
                                                                                onDelete={() => handleDelete(locationId.id)} // Pass only the location ID
                                                                                style={{ margin: 2 }}
                                                                            />

                                                                        )
                                                                    );
                                                                })}
                                                            </CardContent>
                                                        </Box>
                                                    </div>


                                                    <FormHelperText>
                                                        {formik.touched.prefered_location && formik.errors.prefered_location ? 'Locations are Required' : ''}
                                                    </FormHelperText>
                                                </FormControl>


                                                <Box>
                                                    <Button
                                                        disabled={loading}
                                                        sx={{ bgcolor: '#5379BD', borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px', borderRadius: '10px', textTransform: 'capitalize', fontFamily: 'inherit', }}
                                                        variant="contained"
                                                        fullWidth
                                                        type="submit"
                                                    >
                                                        Update Agent
                                                    </Button>
                                                    <LinearProgress sx={{ visibility: loading ? '' : 'hidden' }} />
                                                </Box>
                                            </Stack>
                                        </form>
                                    </Paper>
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default AdminAgentEdit;

